import { Controller } from "stimulus";
import Plyr from "@supercast/plyr";
import { SupercastTimeUpdateEvent, SupercastPlaybackEndedEvent } from "utils/events";
export default class extends Controller {
  static targets = [
    "controlsTemplate",
    "playback0_9",
    "playback1",
    "playback1_1",
    "playback1_5",
    "playback2"
  ]

  static values = {
    timestamp: Number,
    markers: Array,
    itemId: String,
    itemType: String,
    mediaMetadata: Object
  }

  connect() {
    const audio = this.element.querySelector("audio");
    const alreadyInitialized = !!audio?.plyr;

    this.initializePlayer();

    if (!alreadyInitialized) {
      this.player.on("playing", this.onTimeUpdate.bind(this));
      this.player.on("pause", this.onTimeUpdate.bind(this));
      this.player.on("ratechange", this.onRateChange.bind(this));
      this.player.on("timeupdate", this.onTimeUpdate.bind(this));
      this.player.on("ended", this.playbackEnded.bind(this));
    }

    this.setMediaSessionActionHandlers();
    this.setMediaSessionMetadata();
  }

  loadEpisode({ itemId, itemType, audioUrl, timestamp, mediaMetadata }) {
    this.itemIdValue = itemId;
    this.itemTypeValue = itemType;

    if (this.player.source != audioUrl) {
      const audio = this.element.querySelector("audio");
      audio.src = audioUrl;
    }

    if (timestamp != null) {
      this.timestampValue = timestamp;
    }

    if (mediaMetadata) {
      this.mediaMetadataValue = {
        title: mediaMetadata.title,
        artist: mediaMetadata.artist,
        album: mediaMetadata.album,
        artwork: mediaMetadata.artworkUrl
          ? [{ src: mediaMetadata.artworkUrl, sizes: "512x512" }]
          : this.mediaMetadataValue.artwork
      };
      this.setMediaSessionMetadata();
    }

    this.setCurrentTime(this.element.querySelector("audio"));
    this.player.play();

    this.updateFeedTokenConnection();
  }

  updateFeedTokenConnection() {
    const episodeId = this.itemIdValue;
    if (episodeId) {
      fetch(`/subscriber_v2/feed_items/mark_feed_token_connected?episode_id=${episodeId}`, {
        method: "POST"
      });
    }
  }

  // Duplicated in video player - consider keeping in sync
  onRateChange() {
    const speed = this.player.speed;
    this.playback0_9Target.classList.toggle("!hidden", speed !== 0.9)
    this.playback1Target.classList.toggle("!hidden", speed !== 1)
    this.playback1_1Target.classList.toggle("!hidden", speed !== 1.1)
    this.playback1_5Target.classList.toggle("!hidden", speed !== 1.5)
    this.playback2Target.classList.toggle("!hidden", speed !== 2)
    window.dispatchEvent(new CustomEvent("supercast-rate-change", { detail: { playbackRate: speed } }));
  }

  onTimeUpdate() {
    const event = new SupercastTimeUpdateEvent({
      itemId: this.itemIdValue,
      itemType: this.itemTypeValue,
      currentTime: this.player.currentTime,
      duration: this.player.duration,
      isPlaying: !this.player.paused,
      mediaType: "audio"
    });

    window.dispatchEvent(event);
    this.setMediaSessionPlaybackState();
  }

  playbackEnded() {
    const event = new SupercastPlaybackEndedEvent({
      itemId: this.itemIdValue,
      itemType: this.itemTypeValue,
      mediaType: "audio"
    });

    window.dispatchEvent(event);
  }

  play() {
    console.log("Media Playback: play");
    this.player.play();
  }

  pause() {
    this.player.pause();
  }

  jumpBack() {
    this.player.currentTime -= 15;
  }

  jumpForward() {
    this.player.currentTime += 15;
  }

  toggleMute() {
    this.player.muted = !this.player.muted;
  }

  onMuteChange() {
    window.dispatchEvent(new CustomEvent("supercast-mute-change", { detail: { muted: this.player.muted } }));
  }

  seek(time) {
    this.player.currentTime = time;
  }

  // Duplicated in video player - consider keeping in sync
  togglePlaybackRate() {
    if (this.player.speed == 1) {
      this.player.speed = 1.1;
    } else if (this.player.speed == 1.1) {
      this.player.speed = 1.5;
    } else if (this.player.speed == 1.5) {
      this.player.speed = 2;
    } else if (this.player.speed == 2) {
      this.player.speed = 0.9;
    } else {
      this.player.speed = 1;
    }
  }

  initializePlayer() {
    const audio = this.element.querySelector("audio");

    if (audio.plyr) {
      this.player = audio.plyr;
      return;
    }

    if (audio.paused) {
      this.setCurrentTime(audio);
    }

    this.player = new Plyr(audio, {
      controls: this.controlsTemplateTarget.innerHTML,
      markers: this.markersValue,
      seekTime: 15,
      volume: 1
    });

    audio.addEventListener("volumechange", this.onMuteChange.bind(this));
  }

  async setCurrentTime(audio) {
    if (this.hasTimestampValue && this.timestampValue > 0) {
      audio.currentTime = this.timestampValue;
    } else if (window.playbackDb) {
      const listenData = await window.playbackDb.getPlaybackData(this.itemTypeValue, this.itemIdValue);
      if (listenData) {
        const shouldRestart = listenData.duration - listenData.currentTime < 15;
        audio.currentTime = shouldRestart ? 0 : listenData.currentTime;
      }
    }
  }

  setMediaSessionMetadata() {
    if (!("mediaSession" in navigator)) {
      return;
    }

    navigator.mediaSession.metadata = new MediaMetadata({
      title: this.mediaMetadataValue.title,
      artist: this.mediaMetadataValue.artist,
      album: this.mediaMetadataValue.album,
      artwork: this.mediaMetadataValue.artwork
    });
  }

  setMediaSessionActionHandlers() {
    if (!("mediaSession" in navigator)) {
      return;
    }

    navigator.mediaSession.setActionHandler("play", () => {
      this.play();
    });

    navigator.mediaSession.setActionHandler("pause", () => {
      this.pause();
    });

    navigator.mediaSession.setActionHandler("seekbackward", () => {
      this.player.currentTime -= 15;
    });

    navigator.mediaSession.setActionHandler("seekforward", () => {
      this.player.currentTime += 15;
    });
  }

  setMediaSessionPlaybackState() {
    if (!("mediaSession" in navigator)) {
      return;
    }
    if (this.player.currentTime > this.player.duration) {
      /* We can sometimes get this when we're seeking - ignore it and wait for the next update */
      return;
    }

    navigator.mediaSession.setPositionState({
      duration: this.player.duration,
      playbackRate: this.player.speed,
      position: this.player.currentTime
    });

    navigator.mediaSession.playbackState = this.player.paused ? "paused" : "playing";
  }
}
